import React from 'react';

interface PrivacyContentProps {
  variant?: 'web' | 'modal';
}

export const PrivacyContent: React.FC<PrivacyContentProps> = ({ variant = 'web' }) => {
  const isWeb = variant === 'web';
  
  const containerClass = isWeb 
    ? "bg-white p-6 md:p-8 rounded-lg shadow-sm border" 
    : "";
  
  const sectionSpacing = isWeb ? "space-y-6" : "space-y-4";
  const textSize = isWeb ? "text-muted-foreground" : "text-sm text-muted-foreground";
  const headingSize = isWeb ? "text-xl font-semibold mb-3" : "text-lg font-semibold mb-2";
  const listSpacing = isWeb ? "space-y-1" : "space-y-0.5";

  return (
    <div className={containerClass}>
      <p className={`${textSize} mb-6`}>
        forkast is a free personal project currently in beta. This Privacy Policy explains how we collect, use, and protect your data when you use the app.
      </p>
      
      <div className={sectionSpacing}>
        <section>
          <h2 className={headingSize}>1. What We Collect</h2>
          <p className={`${textSize} mb-3`}>
            We collect and store the following when you create an account or use the app:
          </p>
          <ul className={`list-disc pl-6 ${textSize} ${listSpacing}`}>
            <li>Your first and last name, email address, and password (encrypted)</li>
            <li>Recipes you save, import (including scraped hero images), or upload (including images you provide)</li>
            <li>Meal plans and AI-generated wellness assessments based on your data</li>
            <li>Comments or notes you write</li>
            <li>Group/family structures you create</li>
          </ul>
          <p className={`${textSize} mt-3`}>
            We do not collect payment information or real-time location data.
          </p>
        </section>

        <section>
          <h2 className={headingSize}>2. How We Use Your Data</h2>
          <p className={`${textSize} mb-3`}>
            We use your data to:
          </p>
          <ul className={`list-disc pl-6 ${textSize} ${listSpacing}`}>
            <li>Allow you to log in and manage meal plans</li>
            <li>Generate recipe suggestions, grocery lists, and wellness summaries using AI</li>
            <li>Support family/group collaboration</li>
            <li>Improve forkast based on anonymized usage data and AI prompt monitoring (via Langfuse)</li>
            <li>Send essential account-related emails using Resend</li>
          </ul>
          <p className={`${textSize} mt-3`}>
            Your content is shared only within family groups you join. We do not sell or make your data public.
          </p>
        </section>

        <section>
          <h2 className={headingSize}>3. Third-Party Services</h2>
          <p className={`${textSize} mb-3`}>
            We use the following services that may process user data:
          </p>
          <ul className={`list-disc pl-6 ${textSize} ${listSpacing}`}>
            <li>Supabase (hosting/database)</li>
            <li>Vercel & GitHub (deployment)</li>
            <li>Zyte (recipe scraping)</li>
            <li>OpenAI + Langfuse (AI generation and monitoring)</li>
            <li>Tally (feedback forms)</li>
            <li>Resend (emails)</li>
            <li>Meta Pixel (analytics and ad optimization)</li>
            <li>Apple Sign-In & Google Sign-In (authentication)</li>
          </ul>
          <p className={`${textSize} mt-3`}>
            These services may collect usage data like page visits or click events. We do not share your name or email unless you explicitly provide it (e.g., feedback forms).
          </p>
        </section>

        <section>
          <h2 className={headingSize}>4. Data Storage and Security</h2>
          <p className={textSize}>
            Your data is stored securely in Supabase. Passwords are encrypted. forkast is still in beta; some processes (like data exports) are not yet fully automated.
          </p>
        </section>

        <section>
          <h2 className={headingSize}>5. Data Deletion or Access</h2>
          <p className={textSize}>
            You can delete your account and all associated data from account settings. This action is permanent and cannot be undone.
          </p>
        </section>

        <section>
          <h2 className={headingSize}>6. Cookies and Tracking</h2>
          <p className={textSize}>
            forkast uses Meta Pixel and essential cookies to understand usage and improve the app. You can manage Meta ad preferences in your Facebook privacy settings.
          </p>
        </section>

        <section>
          <h2 className={headingSize}>7. Children's Privacy</h2>
          <p className={textSize}>
            forkast is not specifically targeted at children. We do not knowingly collect data from users under 16 without guardian consent.
          </p>
        </section>

        <section>
          <h2 className={headingSize}>8. Changes to This Policy</h2>
          <p className={textSize}>
            We may update this policy as forkast evolves. Major changes will be communicated via the app or email.
          </p>
        </section>
      </div>
    </div>
  );
};